import {
    UNIT_TYPES,
    createId,
    nowIso,
    getState,
    mutate,
    findUnit,
    getDescendantIds,
    enrichUnit,
    buildTree,
    delay,
} from './store';

function assertCanDelete(unit) {
    const children = getState().units.filter((u) => u.parentId === unit.id);
    if (children.length) {
        const error = new Error('Não é possível excluir um item que possui filhos na hierarquia.');
        error.code = 'HAS_CHILDREN';
        throw error;
    }
    const linkedUsers = getState().users.filter(
        (u) => u.boardId === unit.id || u.sectorId === unit.id || u.roleId === unit.id,
    );
    if (linkedUsers.length) {
        const error = new Error('Não é possível excluir um item com colaboradores vinculados.');
        error.code = 'HAS_USERS';
        throw error;
    }
}

function assertValidHierarchy(type, parentId, draft = getState()) {
    if (!parentId) return;
    const parent = draft.units.find((u) => u.id === parentId);
    if (!parent) {
        const error = new Error('Unidade pai não encontrada.');
        error.code = 'INVALID_PARENT';
        throw error;
    }
    if (type === UNIT_TYPES.SECTOR && parent.type !== UNIT_TYPES.COMPANY && parent.type !== UNIT_TYPES.BOARD) {
        const error = new Error('Setor só pode ficar diretamente sob a empresa.');
        error.code = 'INVALID_PARENT';
        throw error;
    }
    if (type === UNIT_TYPES.ROLE && parent.type !== UNIT_TYPES.SECTOR) {
        const error = new Error('Cargo só pode ficar sob um setor.');
        error.code = 'INVALID_PARENT';
        throw error;
    }
}

export default {
    async list({ type, status, parentId } = {}) {
        await delay();
        let items = (getState().units || []).map((unit) => enrichUnit(unit));
        if (type) items = items.filter((u) => u.type === type);
        if (status) items = items.filter((u) => u.status === status);
        if (parentId !== undefined) items = items.filter((u) => u.parentId === parentId);
        return {
            content: items.sort((a, b) => a.order - b.order),
            totalElements: items.length,
            page: 0,
            size: items.length,
        };
    },

    async tree() {
        await delay();
        return { content: buildTree(null) };
    },

    async getById(id) {
        await delay();
        const unit = findUnit(id);
        if (!unit) {
            const error = new Error('Unidade organizacional não encontrada.');
            error.code = 'NOT_FOUND';
            throw error;
        }
        return enrichUnit(unit);
    },

    async create(payload) {
        await delay();
        return mutate((draft) => {
            const type = payload.type || UNIT_TYPES.SECTOR;
            assertValidHierarchy(type, payload.parentId || null, draft);
            const siblings = draft.units.filter((u) => u.parentId === (payload.parentId || null));
            const unit = {
                id: createId('unit'),
                companyId: draft.companyId,
                parentId: payload.parentId || null,
                type,
                name: payload.name,
                description: payload.description || '',
                managerUserId: payload.managerUserId || null,
                status: payload.status || 'active',
                order: payload.order != null ? payload.order : siblings.length,
                courseIds: payload.courseIds || [],
                createdAt: nowIso(),
                updatedAt: nowIso(),
            };
            draft.units.push(unit);
            return enrichUnit(unit, draft);
        });
    },

    async update(id, payload) {
        await delay();
        return mutate((draft) => {
            const index = draft.units.findIndex((u) => u.id === id);
            if (index < 0) {
                const error = new Error('Unidade organizacional não encontrada.');
                error.code = 'NOT_FOUND';
                throw error;
            }
            draft.units[index] = {
                ...draft.units[index],
                ...payload,
                id,
                updatedAt: nowIso(),
            };
            return enrichUnit(draft.units[index], draft);
        });
    },

    async remove(id) {
        await delay();
        return mutate((draft) => {
            const unit = draft.units.find((u) => u.id === id);
            if (!unit) {
                const error = new Error('Unidade organizacional não encontrada.');
                error.code = 'NOT_FOUND';
                throw error;
            }
            assertCanDelete(unit);
            draft.units = draft.units.filter((u) => u.id !== id);
            draft.courseAssignments = draft.courseAssignments.filter(
                (a) => a.organizationalUnitId !== id
                    && a.sectorId !== id
                    && a.roleId !== id,
            );
            return { success: true };
        });
    },

    async updateStatus(id, status) {
        return this.update(id, { status });
    },

    async move(id, { parentId, order }) {
        await delay();
        return mutate((draft) => {
            const unit = draft.units.find((u) => u.id === id);
            if (!unit) {
                const error = new Error('Unidade organizacional não encontrada.');
                error.code = 'NOT_FOUND';
                throw error;
            }
            if (parentId === id) {
                const error = new Error('Não é possível mover um item para dentro de si mesmo.');
                error.code = 'INVALID_MOVE';
                throw error;
            }
            const descendants = getDescendantIds(id);
            if (parentId && descendants.includes(parentId)) {
                const error = new Error('Não é possível mover um item para um descendente.');
                error.code = 'INVALID_MOVE';
                throw error;
            }
            const nextParentId = parentId === undefined ? unit.parentId : parentId;
            if (nextParentId !== unit.parentId) {
                assertValidHierarchy(unit.type, nextParentId, draft);
            }
            unit.parentId = nextParentId;
            if (order != null) unit.order = order;
            unit.updatedAt = nowIso();
            return enrichUnit(unit, draft);
        });
    },
};
